import {
  ApiPropertiesResponse,
  BuyPlanCompany,
  Payment,
  Product,
  Properties,
  subData,
  VehicleData,
} from 'pages/Settings/pages/Add-subscription/types/Add-subscription.types';
import cms from './cms';

/** Lista de propiedades del usuario para asignar a suscripción (agente soporte) */
export async function getPropertyUserList(
  authId: string
): Promise<ApiPropertiesResponse> {
  try {
    const response = await cms.get<ApiPropertiesResponse>(
      `/v3/property-user/${authId}`
    );
    return Array.isArray(response.data) ? response.data : [];
  } catch (error) {
    console.error('Error getPropertyUserList 🚨', error);
    throw error;
  }
}

const VEHICLE_DATA_KEY = 'vehicleData';

const orderVehicles = (vehiculos: VehicleData): VehicleData => {
  const ordenar = (obj: Record<string, string[]>): Record<string, string[]> =>
    Object.keys(obj)
      .map((marca) => marca.trim())
      .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }))
      .reduce<Record<string, string[]>>((acc, marca) => {
        acc[marca] = obj[marca]
          .map((modelo) => modelo.trim())
          .sort((a, b) =>
            a.localeCompare(b, undefined, { sensitivity: 'base' })
          );
        return acc;
      }, {});

  return {
    autos: ordenar(vehiculos.autos),
    motos: ordenar(vehiculos.motos),
  };
};

/* Temporal: cache en localStorage deshabilitado; se mantiene comentado por si se quiere reactivar después. */
// const saveVehicleDataToLocalStorage = (data: VehicleData) => {
//   localStorage.setItem(VEHICLE_DATA_KEY, JSON.stringify(data));
// };
// const getVehicleDataFromLocalStorage = (): VehicleData | null => {
//   const data = localStorage.getItem(VEHICLE_DATA_KEY);
//   return data ? JSON.parse(data) : null;
// };

export const removeVehicleDataFromLocalStorage = () => {
  localStorage.removeItem(VEHICLE_DATA_KEY);
};

/**
 * Data-store key repricing_temporal (temporal, se retira la 1ª semana de nov 2026).
 * stripe_price_ids.show: precios ocultos que sí ven los agentes de emails (CB-149).
 * stripe_price_ids.label_new: precios con la píldora "Nuevo" para todos los agentes (CB-176).
 */
interface RepricingTemporal {
  emails?: string[];
  stripe_price_ids?: {
    show?: string[];
    label_new?: string[];
  };
}

interface RepricingTemporalPriceIds {
  showPriceIds: string[];
  labelNewPriceIds: string[];
}

/**
 * Temporal (CB-149/CB-176): showPriceIds solo si el agente está en emails;
 * labelNewPriceIds para todos. Si la llave no existe (el endpoint responde 500)
 * o falta algún campo, esa lista queda vacía para que borrar la llave deje todo
 * como antes.
 */
async function getRepricingTemporalPriceIds(
  agentEmail?: string
): Promise<RepricingTemporalPriceIds> {
  const normalizedEmail = agentEmail?.trim().toLowerCase();
  try {
    const { data } = await cms.get<RepricingTemporal>(
      `/v2/data-store/repricing_temporal`
    );
    const isAllowedAgent =
      !!normalizedEmail &&
      (data?.emails ?? []).some(
        (email) => email.trim().toLowerCase() === normalizedEmail
      );
    return {
      showPriceIds: isAllowedAgent ? data?.stripe_price_ids?.show ?? [] : [],
      labelNewPriceIds: data?.stripe_price_ids?.label_new ?? [],
    };
  } catch {
    return { showPriceIds: [], labelNewPriceIds: [] };
  }
}

export async function getProductsByCompany(
  companyId: number,
  agentEmail?: string
): Promise<Product[]> {
  try {
    const response = await cms.get(`/v2/products/company/${companyId}`);

    // Temporal: Remove prices by data-store key temporal_hide_prices_buy_plan_v2.
    // Ocultamiento por compañía, identificado por stripe_price_id (unificado con
    // el data-store hide_products_edit_items de nextjs-soda-client-app).
    // Estructura invertida { [stripe_price_id]: companyId[] } para no repetir
    // stripe_price_id por cada compañía que lo oculta.
    const [dataStoreResponse, { showPriceIds, labelNewPriceIds }] =
      await Promise.all([
        cms.get(`/v2/data-store/temporal_hide_prices_buy_plan_v2`),
        getRepricingTemporalPriceIds(agentEmail),
      ]);
    const hiddenCompaniesByPrice: Record<string, string[]> | undefined =
      dataStoreResponse.data;
    const stripePriceIdsToHide = Object.entries(hiddenCompaniesByPrice ?? {})
      .filter(([, companyIds]) => companyIds.includes(String(companyId)))
      .map(([stripePriceId]) => stripePriceId)
      .filter((stripePriceId) => !showPriceIds.includes(stripePriceId));

    // CB-171/CB-176 (temporal): los precios de repricing_temporal.stripe_price_ids.label_new llevan la píldora "Nuevo".
    response.data = response.data.map((product: Product) => ({
      ...product,
      prices: product.prices
        .filter(
          (price) => !stripePriceIdsToHide.includes(price.stripe_price_id)
        )
        .map((price) => ({
          ...price,
          isRepricingNew: labelNewPriceIds.includes(price.stripe_price_id),
        })),
    }));
    return response.data;
  } catch (error) {
    console.error(error);
    throw new Error('Failed to get products by companyId 🚨');
  }
}

/**
 * Compañías para el selector de buy plan. El endpoint exige rol admin o customer service: en la
 * impersonation desde nextjs-soda-client-app el token del store ya es el del agente
 * (agentStrapiToken), así que el cliente `cms` lo envía tal cual. Para cualquier otro rol el
 * endpoint responde 403 y se devuelve [] (el selector no se muestra).
 */
export async function getBuyPlanCompanies(): Promise<BuyPlanCompany[]> {
  try {
    const { data } = await cms.get<{ results?: BuyPlanCompany[] }>(
      `/v2/backoffice/companies`
    );
    return Array.isArray(data?.results) ? data.results : [];
  } catch (error) {
    console.error('Error getBuyPlanCompanies 🚨', error);
    return [];
  }
}

// TEMPORAL — eliminar con CB-186
/**
 * CB-190: params de company permitidos en Buy Plan para el país de la cuenta, desde la llave
 * `buy_plan_companies` ({ "PR": ["connectpr", "soda-assistance"] }). Llave ausente (el endpoint
 * responde 500), mal formada o sin el país → [] y no se muestra el selector.
 */
export async function getBuyPlanCompanyParams(
  countryAcronym?: string | null
): Promise<string[]> {
  const country = countryAcronym?.trim().toUpperCase();
  if (!country) return [];
  try {
    const { data } = await cms.get<Record<string, unknown>>(
      `/v2/data-store/buy_plan_companies`
    );
    const params = data?.[country];
    return Array.isArray(params)
      ? params.filter(
          (param): param is string =>
            typeof param === 'string' && param.trim() !== ''
        )
      : [];
  } catch {
    return [];
  }
}

export async function getDataByKey(key: string): Promise<VehicleData> {
  try {
    /* Temporal: no usar cache; siempre obtener datos del servidor. */
    // const cachedData = getVehicleDataFromLocalStorage();
    // if (cachedData) return cachedData;

    const response = await cms.get(`/v2/data-store/${key}`);
    const vehiclesOrdered = orderVehicles(response.data);
    // saveVehicleDataToLocalStorage(vehiclesOrdered);
    return vehiclesOrdered;
  } catch (error) {
    console.error(error);
    throw new Error('Failed to get data of autos and motos 🚨');
  }
}

interface CreateSubProps {
  properties: Properties;
  sub: subData;
  payment: Payment;
  authId: string;
}

export async function createSub({
  properties,
  sub,
  authId,
  payment,
}: CreateSubProps) {
  try {
    const data = { properties, sub, payment };
    const response = await cms.post(
      `/v2/add-subscription-buy-plan/${authId}`,
      data
    );

    return response.data;
  } catch (error) {
    console.error('Error createSub 🚨', error);
    throw error;
  }
}
